import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../../lib/api.js';

// Load + edit + autosave the day's Edition. Server is source of truth;
// optimistic local state, debounced per-article writes (800ms), bulk save
// on demand. Last-write-wins (no version field on Edition).

const AUTOSAVE_MS = 800;

function todayIsoCentral() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

export function useEditionDraft(isoDate) {
  const date = isoDate || todayIsoCentral();
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const pendingArticlePatches = useRef(new Map()); // articleId -> { field: value }
  const pendingEditionPatch = useRef({}); // bulk fields keyed by name
  const flushTimer = useRef(null);
  const inFlight = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    try {
      const path = isoDate ? `/editions/${isoDate}` : '/editions/today';
      const data = await api.get(path);
      if (!data.edition) {
        setNotFound(true);
      } else {
        setEdition(data.edition);
      }
    } catch (err) {
      if (err.status === 404) setNotFound(true);
      else setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isoDate]);

  useEffect(() => {
    load();
    return () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
    };
  }, [load]);

  // ── Local optimistic mutators ─────────────────────────────────────────────

  const updateEditionField = useCallback((field, value) => {
    setEdition((prev) => (prev ? { ...prev, [field]: value } : prev));
    pendingEditionPatch.current[field] = value;
    scheduleFlush();
  }, []);

  const updateIntroSummary = useCallback((value) => {
    setEdition((prev) =>
      prev ? { ...prev, intro: { ...(prev.intro || {}), summary: value } } : prev
    );
    pendingEditionPatch.current.intro = {
      ...(pendingEditionPatch.current.intro || {}),
      summary: value,
    };
    scheduleFlush();
  }, []);

  const updateIntroRedThread = useCallback(({ summary, anchorUrls, links }) => {
    setEdition((prev) => {
      if (!prev) return prev;
      const intro = { ...(prev.intro || {}) };
      if (summary !== undefined) intro.summary = summary;
      if (anchorUrls !== undefined) intro.anchorUrls = anchorUrls;
      if (links !== undefined) intro.links = links;
      return { ...prev, intro };
    });
    pendingEditionPatch.current.intro = {
      ...(pendingEditionPatch.current.intro || {}),
      ...(summary !== undefined ? { summary } : {}),
      ...(anchorUrls !== undefined ? { anchorUrls } : {}),
      ...(links !== undefined ? { links } : {}),
    };
    scheduleFlush();
  }, []);

  const updateArticleField = useCallback((articleId, field, value) => {
    setEdition((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        articles: prev.articles.map((a) =>
          String(a._id) === String(articleId) ? { ...a, [field]: value } : a
        ),
      };
    });
    const id = String(articleId);
    const existing = pendingArticlePatches.current.get(id) || {};
    existing[field] = value;
    pendingArticlePatches.current.set(id, existing);
    scheduleFlush();
  }, []);

  // ── Save plumbing ─────────────────────────────────────────────────────────

  function markDirty() {
    setSavedAt(null);
    setSaveError(null);
  }

  function scheduleFlush() {
    markDirty();
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushPending, AUTOSAVE_MS);
  }

  async function flushPending() {
    const articlePatches = Array.from(pendingArticlePatches.current.entries());
    const editionPatch = { ...pendingEditionPatch.current };
    pendingArticlePatches.current.clear();
    pendingEditionPatch.current = {};

    if (articlePatches.length === 0 && Object.keys(editionPatch).length === 0) return;

    inFlight.current += 1;
    setSaving(true);
    setSaveError(null);

    try {
      // Per-article writes first — these are the debounced-autosave path so
      // each article only touches its own document slot.
      await Promise.all(
        articlePatches.map(([articleId, patch]) =>
          api.put(`/editions/${date}/articles/${articleId}`, patch)
        )
      );
      if (Object.keys(editionPatch).length > 0) {
        await api.put(`/editions/${date}`, editionPatch);
      }
      setSavedAt(new Date());
    } catch (err) {
      setSaveError(err.message);
    } finally {
      inFlight.current = Math.max(0, inFlight.current - 1);
      if (inFlight.current === 0) setSaving(false);
    }
  }

  const saveNow = useCallback(async () => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    await flushPending();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Article CRUD (Add Source, Bench-promote, remove) ──────────────────────

  const insertArticle = useCallback(
    async (article) => {
      const res = await api.post(`/editions/${date}/articles`, article);
      setEdition(res.edition);
      return res.article;
    },
    [date]
  );

  const deleteArticle = useCallback(
    async (articleId) => {
      const res = await api.del(`/editions/${date}/articles/${articleId}`);
      if (res?.edition) setEdition(res.edition);
      return res;
    },
    [date]
  );

  // Editor cut flow: removes the article from today's edition AND returns
  // its matching bench row to the bench (or creates a Tier 6 row if there
  // wasn't one). Any blackout keywords the editor supplied land in the
  // KeywordBlackout collection.
  //
  // Server returns the freshly-saved edition so the article list re-renders
  // without a round-trip; it also returns { bench, blackouts } summaries which
  // the caller may use for UX feedback.
  const cutArticle = useCallback(
    async (articleId, { blackoutKeywords = [] } = {}) => {
      const res = await api.post(
        `/editions/${date}/articles/${articleId}/cut`,
        { blackoutKeywords }
      );
      if (res?.edition) setEdition(res.edition);
      return res;
    },
    [date]
  );

  const reorderArticles = useCallback(
    async (rows) => {
      const res = await api.patch(`/editions/${date}/articles/reorder`, rows);
      if (res?.edition) setEdition(res.edition);
      return res;
    },
    [date]
  );

  // Externally-driven replace — used by the Bench modal when a promote-to-today
  // returns the freshly-saved edition so the article list rehydrates in place.
  const replaceEdition = useCallback((next) => {
    if (next) setEdition(next);
  }, []);

  return {
    date,
    edition,
    loading,
    loadError,
    notFound,
    saving,
    savedAt,
    saveError,
    reload: load,
    updateEditionField,
    updateIntroSummary,
    updateIntroRedThread,
    updateArticleField,
    saveNow,
    insertArticle,
    deleteArticle,
    cutArticle,
    reorderArticles,
    replaceEdition,
  };
}
